import {supportsLivePatientWatch} from '@/core/services/shellCapabilities';
import type {LiveOperator, LivePatientEvent, LiveWatchStatus} from '@/live/interfaces';

/**
 * Renderer-side entry point of the LIVE patient context (AP-7768).
 *
 * The shell detects, from this workstation's own LGO traffic, which patient is
 * selected at the counter and pushes a {nir, posteId, lgo, timestamp}
 * event. This is the seam between that capability and the LIVE feature: it
 * starts the watch, hands each selection to the caller, and returns how to stop.
 *
 * On a shell without the capability it does nothing and reports a null status,
 * so the LIVE context keeps to the on-demand card read there. The capability is
 * detected by method presence, never by a version comparison.
 */
export interface LivePatientContext {
    /** The shell's watch status, or null on a shell without the capability. */
    status: LiveWatchStatus | null;
    /** Stop listening and stop the watch. */
    stop(): void;
}

/** How often to look for an Apodis Live service that was absent. */
const SERVICE_RETRY_MS = 60_000;

export async function startLivePatientContext(
    onSelection: (event: LivePatientEvent) => void,
    onOperator?: (operator: LiveOperator) => void,
): Promise<LivePatientContext> {
    const bridge = window.ipcRenderer;

    if (!supportsLivePatientWatch() || !bridge.onLivePatientSelected || !bridge.startLivePatientWatch || !bridge.stopLivePatientWatch) {
        return {status: null, stop: () => {}};
    }

    const unsubscribe = bridge.onLivePatientSelected(onSelection);
    // Older shells cannot report the operator: nothing is shown there.
    const unsubscribeOperator = onOperator ? bridge.onLiveOperatorChanged?.(onOperator) : undefined;
    const status = await bridge.startLivePatientWatch();

    // The operator seen before this window started the watch (a reload).
    if (onOperator && status.operator) {
        onOperator(status.operator);
    }

    // The shell does not try again when the service is absent. It may only be
    // starting: the Apodis Suite installer launches Connect a few seconds
    // before it starts the service. Until it answers, the OCR goes on.
    let retry: ReturnType<typeof setInterval> | null = null;

    if (status.reason === 'service-unavailable') {
        retry = setInterval(async () => {
            const retried = await bridge.startLivePatientWatch?.().catch(() => null);

            if (!retried || retried.reason === 'service-unavailable' || !retry) {
                return;
            }

            clearInterval(retry);
            retry = null;

            if (onOperator && retried.operator) {
                onOperator(retried.operator);
            }
        }, SERVICE_RETRY_MS);
    }

    return {
        status,
        stop: () => {
            if (retry) {
                clearInterval(retry);
                retry = null;
            }

            unsubscribe();
            unsubscribeOperator?.();
            void bridge.stopLivePatientWatch?.();
        },
    };
}
